import { BALL_IN_COURT_LABELS, describeDevice, eventEntityLabel, eventFieldLabel, eventMoneyText, type BallInCourt, type EventLogRow, type TurnSpan } from '@nest-recruit/shared';
import { EMPTY, formatDateTime } from '../../../../lib/format';

// Admin · Event log, in words: the record line, the before/after table, the extra details and the whose-turn strip.
// Pure functions over a row, so each is tested without rendering.

/** "a3f9…c21e": enough of an id to tell two apart, short enough for a table cell. */
export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

/** The record's name, or its kind and a short id when it has no name (a session, a background job). */
export function recordNameOf(row: Pick<EventLogRow, 'record' | 'entityType' | 'entityId'>): string {
  return row.record?.name ?? `${eventEntityLabel(row.entityType)} ${shortId(row.entityId)}`;
}

/** "Application · Senior product designer · Nest Digital": the kind, the job or reference, the venture. */
export function recordLineOf(row: Pick<EventLogRow, 'record' | 'entityType' | 'ventureName'>): string {
  return [eventEntityLabel(row.entityType), row.record?.detail, row.ventureName].filter((part): part is string => typeof part === 'string' && part !== '').join(' · ');
}

/** Who did it, as the table names them. The system is labelled as such everywhere (06-qol §12). */
export function actorLabelOf(row: Pick<EventLogRow, 'actorType' | 'actorName'>): string {
  if (row.actorType === 'system') return 'The system';
  return row.actorName ?? 'Someone no longer here';
}

/** Whose turn it became, or null when the event moved no work (or moved it to nobody). */
export function nextTurnOf(row: Pick<EventLogRow, 'ballInCourt'>): string | null {
  if (row.ballInCourt === null || row.ballInCourt === 'none') return null;
  return BALL_IN_COURT_LABELS[row.ballInCourt];
}

/** "rejectionReasonCode" → "Rejection reason code"; "budgetMaxMinor" → "Top of budget" (review W13, shared with the export). */
export function fieldLabel(key: string): string {
  return eventFieldLabel(key);
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;
/** Longer than this, a value is cut in the table; the whole of it is in its title and in the raw data. */
const VALUE_MAX = 120;

/** One recorded value as text: — for nothing, Yes/No, a time with its zone (#8), and anything else as compact JSON. */
export function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return ISO_INSTANT.test(value) ? formatDateTime(value) : value;
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return JSON.stringify(value);
}

/** One field of a recorded side: an amount in minor units reads as money with its record's currency (W13), the rest as `valueText`. */
function recordedText(key: string, data: Readonly<Record<string, unknown>>): string {
  return eventMoneyText(key, data[key], data) ?? valueText(data[key]);
}

export function clipped(text: string): string {
  return text.length > VALUE_MAX ? `${text.slice(0, VALUE_MAX - 1)}…` : text;
}

function objectOf(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

export interface FieldRow {
  readonly key: string;
  readonly label: string;
  readonly before: string;
  readonly after: string;
  readonly isChanged: boolean;
  readonly isBeforeHidden: boolean;
  readonly isAfterHidden: boolean;
}

/**
 * What changed, as a table. With both sides recorded: every field either side names, Before and After, the changed
 * ones marked. With one side only (something created, or removed): `kind` says which, and only that side is filled.
 * A value hidden from the viewer arrives as — and is marked so (non-negotiable #5).
 */
export interface FieldTable {
  readonly kind: 'diff' | 'after' | 'before' | 'none';
  readonly rows: readonly FieldRow[];
}

export function fieldTableOf(row: Pick<EventLogRow, 'payloadBefore' | 'payloadAfter' | 'restricted'>): FieldTable {
  const before = objectOf(row.payloadBefore);
  const after = objectOf(row.payloadAfter);
  const hidden = new Set(row.restricted);
  const kind: FieldTable['kind'] = before && after ? 'diff' : after ? 'after' : before ? 'before' : 'none';
  if (kind === 'none') return { kind, rows: [] };

  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  return {
    kind,
    rows: keys.map((key) => {
      const isBeforeHidden = hidden.has(`payloadBefore.${key}`);
      const isAfterHidden = hidden.has(`payloadAfter.${key}`);
      return {
        key,
        label: fieldLabel(key),
        before: before ? recordedText(key, before) : EMPTY,
        after: after ? recordedText(key, after) : EMPTY,
        // Hidden on both sides, a change can't be told and isn't claimed.
        isChanged: kind === 'diff' && !(isBeforeHidden && isAfterHidden) && !same(before?.[key], after?.[key]),
        isBeforeHidden,
        isAfterHidden,
      };
    }),
  };
}

export interface Fact {
  readonly label: string;
  readonly value: string;
  readonly isCode?: boolean;
}

/** Metadata the "About this event" list names itself, or that is only plumbing. */
const KNOWN_METADATA = new Set(['ip', 'userAgent', 'requestId', 'impersonatedUserId', 'impersonatorUserId']);

function textAt(data: Readonly<Record<string, unknown>> | null, key: string): string | null {
  const value = data?.[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** "About this event": the record, who, whose turn, the venture, then where it came from and anything else recorded. */
export function factsOf(row: EventLogRow): readonly Fact[] {
  const metadata = objectOf(row.metadata);
  const record = row.record?.detail ? `${recordNameOf(row)} — ${row.record.detail}` : recordNameOf(row);
  const facts: Fact[] = [
    { label: 'Record', value: record },
    { label: 'Record id', value: row.entityId, isCode: true },
    { label: 'Who', value: actorLabelOf(row) },
    { label: 'Whose turn', value: row.ballInCourt ? BALL_IN_COURT_LABELS[row.ballInCourt as BallInCourt] : EMPTY },
    { label: 'Venture', value: row.ventureName ?? EMPTY },
  ];
  if (metadata?.impersonatedUserId) facts.push({ label: 'While viewing as', value: row.viewedAsName ?? 'Someone no longer here' });
  facts.push(
    { label: 'Request', value: textAt(metadata, 'requestId') ?? EMPTY, isCode: true },
    { label: 'Address', value: textAt(metadata, 'ip') ?? EMPTY, isCode: true },
    { label: 'Device', value: describeDevice(textAt(metadata, 'userAgent')) ?? EMPTY },
  );
  for (const [key, value] of Object.entries(metadata ?? {})) {
    if (KNOWN_METADATA.has(key)) continue;
    facts.push({ label: fieldLabel(key), value: clipped(valueText(value)) });
  }
  return facts;
}

/** "3 days", "5 hours", "12 minutes", "0 days" — the strip's and the header's durations. */
export function durationText(minutes: number): string {
  const days = Math.floor(minutes / (24 * 60));
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'}`;
  const hours = Math.floor(minutes / 60);
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

/** "now with Hiring manager for 2 days", "Nobody's turn now", or null when the record never carried a turn. */
export function nowWithText(spans: readonly TurnSpan[], lastHolder: BallInCourt | null): string | null {
  if (lastHolder === 'none') return "nobody's turn now";
  const last = spans[spans.length - 1];
  if (!last || last.to !== null) return null;
  return `now with ${BALL_IN_COURT_LABELS[last.holder]} for ${durationText(last.minutes)}`;
}
